import { useEffect, useState } from "react";
import { Columns2, Copy, Loader2, Trash2, Globe, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "../../../lib/api";
import { media } from "../ExtrasShell";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";

export const CoverTicks = ({ p, save }) => {
  const [v, setV] = useState((p.cover_ticks || []).join(", "));
  const commit = () => {
    const ticks = v.split(",").map((x) => x.trim()).filter(Boolean);
    if (ticks.join(",") !== (p.cover_ticks || []).join(",")) save({ cover_ticks: ticks });
  };
  return (
    <div data-testid="ex-cover-ticks" className="ex-card px-5 py-4 mb-5">
      <label className="block text-sm text-[#8B6B4A] mb-2">Cover timeline labels (comma separated, leave empty for none)</label>
      <input data-testid="ex-cover-ticks-input" value={v} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
        placeholder="e.g. 8h, 12h, 18h, 24h" className="w-full rounded-full border border-[#E6DDCF] bg-[#FBF8F2] px-5 py-2.5 text-sm outline-none focus:border-[#8B6B4A]" />
    </div>
  );
};

export const CompareButton = ({ p }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid="ex-compare-original" onClick={() => setOpen(true)} className="ml-auto inline-flex items-center gap-1.5 text-sm text-stone-700 hover:underline"><Columns2 size={14} /> Compare with original</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="ex-compare-dialog" onKeyDownCapture={(e) => e.key === "Escape" && setOpen(false)} className="max-w-[95vw] w-[95vw] h-[90vh] flex flex-col">
          <DialogHeader><DialogTitle className="ex-display">Original vs restyled</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 flex-1 min-h-0">
            {[["Your upload", p.original_pdf_id], ["PB restyled", p.deliverable_id]].map(([t, id]) => (
              <div key={t} className="flex flex-col min-h-0">
                <div className="ex-eyebrow mb-1">{t}</div>
                {id ? <iframe data-testid={`ex-compare-${id === p.original_pdf_id ? "original" : "restyled"}`} title={t} src={media(id)} className="flex-1 w-full border border-[#E6DDCF]" />
                  : <div className="flex-1 grid place-items-center border border-dashed border-stone-300 text-sm text-stone-500">The restyled PDF is built when you publish or press Rebuild PDF.</div>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const ImagesTab = ({ p, save }) => {
  const [imgs, setImgs] = useState(p.images || []);
  const set = (i, caption) => setImgs(imgs.map((x, j) => (j === i ? { ...x, caption } : x)));
  if (!imgs.length) return <div data-testid="ex-images-empty" className="text-sm text-stone-500">No pictures were taken from the uploaded PDF for this product.</div>;
  return (
    <div data-testid="ex-images-tab">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {imgs.map((im, i) => (
          <div key={im.id} data-testid={`ex-image-${i}`} className="ex-card p-3 flex flex-col">
            <div className="aspect-square bg-[#FBF8F2] grid place-items-center mb-2"><img src={media(im.id)} alt={im.caption} className="max-h-full max-w-full object-contain" /></div>
            <div className="ex-mono text-[10px] text-stone-500 mb-1">{im.page ? `From page ${im.page}` : im.source}</div>
            <textarea data-testid={`ex-image-${i}-caption`} rows={3} value={im.caption || ""} onChange={(e) => set(i, e.target.value)} className="w-full border border-stone-300 rounded-sm p-2 text-xs" />
            <button data-testid={`ex-image-${i}-remove`} onClick={() => setImgs(imgs.filter((_, j) => j !== i))} className="mt-2 self-start inline-flex items-center gap-1 text-xs text-stone-500 hover:text-red-700"><Trash2 size={12} /> Remove</button>
          </div>
        ))}
      </div>
      <button data-testid="ex-images-save" onClick={() => save({ images: imgs })} className="ex-btn ex-btn-dark mt-5">Save pictures</button>
    </div>
  );
};

const Row = ({ label, value, testid }) => (
  <div className="grid md:grid-cols-[180px_1fr_auto] gap-3 items-center py-2.5 border-b border-[#EFE9E0] text-sm">
    <div className="text-stone-500">{label}</div>
    <div data-testid={testid} className="ex-mono text-xs break-all">{value || "—"}</div>
    {value && /^https?:/.test(value) ? <div className="flex gap-2">
      <button onClick={() => { navigator.clipboard.writeText(value); toast.success("Copied"); }} className="text-stone-500 hover:text-stone-900"><Copy size={13} /></button>
      <a href={value} target="_blank" rel="noreferrer" className="text-stone-500 hover:text-stone-900"><ExternalLink size={13} /></a></div> : <span />}
  </div>
);

export const SiteTab = ({ p }) => {
  const [d, setD] = useState(null);
  useEffect(() => { http.get(`/extras/products/${p.id}/site`).then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))); }, [p.id, p.published, p.updated_at]);
  if (!d) return <div className="flex items-center gap-2 text-sm text-stone-500"><Loader2 size={14} className="animate-spin" /> Loading…</div>;
  const it = d.item || {};
  return (
    <div data-testid="ex-site-tab" className="ex-card p-6">
      <div className="flex items-center gap-3 mb-4"><Globe size={16} className="text-[#8B6B4A]" /><span className="ex-display text-2xl">pbtravelangel.com</span>
        <span data-testid="ex-site-state" className={`ml-auto ex-mono text-[10px] tracking-[0.2em] px-2 py-1 rounded border ${d.published ? "text-emerald-700 border-emerald-200 bg-emerald-50" : "text-stone-600 border-stone-300"}`}>{d.published ? "SENT TO THE WEBSITE" : "NOT ON THE WEBSITE"}</span></div>
      {!d.published && <p className="text-sm text-stone-500 mb-4">Press Publish to store at the top. The website then picks this product up from the feed below on its next refresh.</p>}
      <Row label="Product feed" value={d.feed_url} testid="ex-site-feed" />
      <Row label="This product" value={d.item_url} testid="ex-site-item" />
      <Row label="Slug" value={d.slug} testid="ex-site-slug" />
      <Row label="Collection" value={it.collection} testid="ex-site-collection" />
      <Row label="Price" value={it.listing?.price_aud != null ? `A$${it.listing.price_aud}` : null} testid="ex-site-price" />
      <Row label="Picture" value={it.image_url} testid="ex-site-image" />
      <Row label="Free sample" value={it.sample_url} testid="ex-site-sample" />
      <Row label="Edition" value={it.edition ? `Edition ${it.edition}${it.last_updated ? ` · ${new Date(it.last_updated).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}` : ""}` : null} testid="ex-site-edition" />
      <Row label="App edition" value={it.app?.available ? `${it.app.name || "Yes"} · A$${it.app.price_aud ?? "—"}` : "Not offered"} testid="ex-site-app" />
    </div>
  );
};
